import { useEffect, useRef, useState } from "react";
import type { TranslateFn } from "../../i18n";
import { UploadIcon } from "../icons";
import { checkAgeBeforeUpload, compressImage } from "../../image-utils";

/**
 * Single source of truth for photo upload UX across the SDK. Handles:
 *   • drag / drop (with proper enter+over+leave+drop handlers — drop fails
 *     silently in Chromium without preventDefault on every event)
 *   • click-to-open file picker
 *   • optional Gemini age-check (when apiUrl + apiKey are supplied)
 *   • size + type validation
 *   • internal preview, processing spinner, rejection card
 *
 * Used by PhotoGuideView, SizeResultView's photo-guide overlay, and any
 * other place a customer is asked for a photo. Replaces the duplicated
 * handlePhotoSelect blocks in BodyProfileView / CreateProfileWizard /
 * AccessorySizeView (incrementally — those still have their own state).
 */

export type PhotoUploadZoneVariant = "block" | "inline";

export function PhotoUploadZone({
	apiUrl,
	apiKey,
	previewUrl,
	requireAgeConfirm = false,
	ageConfirmed,
	disabled = false,
	hintLine,
	emptyTitle,
	variant = "block",
	onPhotoAccepted,
	onClearPhoto,
	t,
}: {
	/** When provided with apiKey, runs Gemini age-check before accepting. */
	apiUrl?: string;
	apiKey?: string;
	/** External preview URL (e.g. parent already has a previewUrl from an
	 *  earlier flow). Pass null to render the empty drop state. */
	previewUrl?: string | null;
	/** When true, the zone refuses uploads until ageConfirmed is true. */
	requireAgeConfirm?: boolean;
	ageConfirmed?: boolean;
	/** Disables click + drop (e.g. while parent is processing). */
	disabled?: boolean;
	/** Optional copy under the empty-state title. */
	hintLine?: string;
	/** Custom empty-state title. Defaults to "Upload your photo". */
	emptyTitle?: string;
	/** "block" (default) — full upload card with title/hint/icon.
	 *  "inline" — just the drop area sized to its parent (preview-only). */
	variant?: PhotoUploadZoneVariant;
	/** Called with the validated File once it passes age-check + size/type
	 *  validation. Parent is responsible for compressing or sending it. */
	onPhotoAccepted: (file: File) => void;
	/** Called when the user removes the current photo (via the X on preview). */
	onClearPhoto?: () => void;
	t: TranslateFn;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const dragDepthRef = useRef(0);
	const [dragOver, setDragOver] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [statusText, setStatusText] = useState<string>("");
	const [rejection, setRejection] = useState<string | null>(null);
	const [localError, setLocalError] = useState<string | null>(null);

	// Reset rejection / error whenever the parent clears the previewUrl.
	useEffect(() => {
		if (!previewUrl) {
			setRejection(null);
			setLocalError(null);
		}
	}, [previewUrl]);

	const acceptFile = async (file: File) => {
		if (disabled || processing) return;
		setLocalError(null);
		setRejection(null);
		if (requireAgeConfirm && ageConfirmed !== true) {
			setLocalError(t("Please confirm that the person in the photo is 18 or older before uploading."));
			return;
		}
		if (!file.type.startsWith("image/")) {
			setLocalError(t("Please upload an image file"));
			return;
		}
		if (file.size > 10 * 1024 * 1024) {
			setLocalError(t("Image must be under 10MB"));
			return;
		}

		// Age check — only when API config is present.
		if (apiUrl && apiKey) {
			setProcessing(true);
			setStatusText(t("Analyzing photo…"));
			try {
				const ageResult = await checkAgeBeforeUpload(file, apiUrl, apiKey);
				if (!ageResult.isAdult) {
					const reason = ageResult.reasoning?.trim()
						|| t("This photo appears to be of a minor. Please upload a photo of someone 18 or older.");
					setRejection(reason);
					setProcessing(false);
					setStatusText("");
					return;
				}
				// Pre-compress in the background — parent is free to compress
				// again, but most consumers want a 1024px JPEG.
				try { await compressImage(file, { maxDimension: 1024, quality: 0.85 }); } catch { /* non-fatal */ }
			} catch {
				// Fail-open: network blip shouldn't block the user.
			} finally {
				setProcessing(false);
				setStatusText("");
			}
		}

		onPhotoAccepted(file);
	};

	const onClick = () => {
		if (disabled || processing) return;
		inputRef.current?.click();
	};

	// Drag handlers — every event gets preventDefault so Chromium accepts
	// the drop. `dragDepthRef` counts enter/leave so child elements don't
	// flicker the highlight on every nested SVG/text.
	const onDragEnter = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (disabled || processing) return;
		dragDepthRef.current += 1;
		setDragOver(true);
	};
	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
	};
	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
		if (dragDepthRef.current === 0) setDragOver(false);
	};
	const onDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		dragDepthRef.current = 0;
		setDragOver(false);
		if (disabled || processing) return;
		const file = e.dataTransfer?.files?.[0];
		if (file) void acceptFile(file);
	};
	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) void acceptFile(file);
		// Reset so picking the same file twice still triggers onChange.
		if (inputRef.current) inputRef.current.value = "";
	};

	const removePhoto = () => {
		setRejection(null);
		setLocalError(null);
		onClearPhoto?.();
	};

	const isInline = variant === "inline";

	return (
		<div
			className={`ps-photo-zone${dragOver ? " ps-photo-zone-drag" : ""}${isInline ? " ps-photo-zone-inline" : ""}${previewUrl ? " ps-photo-zone-has" : ""}`}
			onClick={previewUrl ? undefined : onClick}
			onDragEnter={onDragEnter}
			onDragOver={onDragOver}
			onDragLeave={onDragLeave}
			onDrop={onDrop}
		>
			<input
				ref={inputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp"
				style={{ display: "none" }}
				onChange={onChange}
			/>

			{previewUrl && !rejection && (
				<>
					<img src={previewUrl} alt={t("Your photo")} className="ps-photo-zone-img" />
					<div className="ps-photo-zone-hover-overlay" onClick={onClick} title={t("Click to change photo")}>
						<span>{t("Click to change photo")}</span>
					</div>
					{onClearPhoto && (
						<button
							type="button"
							className="ps-photo-zone-remove"
							onClick={(e) => { e.stopPropagation(); removePhoto(); }}
							aria-label={t("Remove photo")}
						>
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
								<line x1="18" y1="6" x2="6" y2="18" />
								<line x1="6" y1="6" x2="18" y2="18" />
							</svg>
						</button>
					)}
				</>
			)}

			{!previewUrl && !rejection && !processing && (
				<div className="ps-photo-zone-empty">
					<UploadIcon size={32} />
					<span className="ps-photo-zone-title">{emptyTitle || t("Upload your photo")}</span>
					{hintLine && <span className="ps-photo-zone-hint">{hintLine}</span>}
					<span className="ps-photo-zone-hint">{t("JPEG, PNG up to 10MB · click or drop")}</span>
				</div>
			)}

			{processing && (
				<div className="ps-photo-zone-processing">
					<div className="ps-photo-zone-spinner" />
					<span className="ps-photo-zone-status">{statusText}</span>
				</div>
			)}

			{rejection && (
				<div className="ps-photo-zone-rejection" onClick={(e) => e.stopPropagation()}>
					<div className="ps-photo-zone-rejection-icon" aria-hidden="true">!</div>
					<div className="ps-photo-zone-rejection-title">{t("Different photo needed")}</div>
					<div className="ps-photo-zone-rejection-msg">{rejection}</div>
					<button
						type="button"
						className="ps-photo-zone-rejection-cta"
						onClick={() => { setRejection(null); inputRef.current?.click(); }}
					>
						{t("Choose another photo")}
					</button>
				</div>
			)}

			{localError && !rejection && (
				<div className="ps-photo-zone-error" onClick={(e) => e.stopPropagation()}>
					{localError}
				</div>
			)}
		</div>
	);
}
